import type { TFunction } from "i18next";
import { stepKindAxes } from "../../../api/content/processTermWords";
import type { GraphNode } from "../model/workflowGraph";

/** The kinds of step a card shows, each with its own slight colour (F61). */
export type StepKind = "tool" | "agent" | "person";

/** A step definition's `kind`, as the server names it. */
export type StepDefinitionKind = "agent" | "human" | "tool" | "talk";

/**
 * A step definition's kind as the cards colour it: a `human` step is a person's. A `talk` step
 * has no card: a review step offers it, and no Workflow places it (F105).
 */
export function stepKindOfDefinition(kind: StepDefinitionKind): StepKind | null {
  switch (kind) {
    case "agent":
    case "tool":
      return kind;
    case "human":
      return "person";
    case "talk":
      return null;
  }
}

/** A step card's kind; `null` for inputs, sub-workflows and steps that could not be read. */
export function stepKindOf(node: GraphNode): StepKind | null {
  return node.kind !== "step" || node.step === null ? null : stepKindOfDefinition(node.step.definition.kind);
}

/**
 * Whether a step's kind mark adds the raised hand (`StepKindMark`): its placement needs a person's input, as module
 * B's plan says (WD-1+3), and its kind doesn't say so already; never before the kinds' table arrives.
 */
export function addsPersonInputMark(kind: StepDefinitionKind, needsPerson: boolean): boolean {
  const implied = stepKindAxes(kind);
  return needsPerson && implied !== null && implied.needsPerson !== true;
}

/** What a step kind's mark itself says on the two axes, in words, for the legend; none before the table arrives. */
export function kindAxesWords(kind: StepDefinitionKind, t: TFunction): readonly string[] {
  const implied = stepKindAxes(kind);
  if (implied === null) return [];
  return [...(implied.needsPerson === true ? [t("workflows.axes.needsPerson")] : []), t(implied.usesLlm ? "workflows.axes.withLlm" : "workflows.axes.withoutLlm")];
}
